import styles from './index.module.css'
import {
  CUSTOM_CHANNEL,
  INLINE_SEARCHABLE_GROUP_TYPES,
  SAVED_TO_DISCO,
} from '@disco/constants/GroupTypes'
import { getPlan } from '@disco/state-management/selectors/business'
import { getGroup } from '@disco/state-management/selectors/groups'
import { PLUS_ID } from '@disco/utils/featuresAccessLevel'
import { isChannelShared } from '@disco/utils/groups'
import clsx from 'clsx'
import { Theme } from '@radix-ui/themes'

import React from 'react'
import { MdOutlineWarningAmber } from '@react-icons/all-files/md/MdOutlineWarningAmber'
import { MdInfoOutline } from '@react-icons/all-files/md/MdInfoOutline'
import { useSelector } from 'hooks/use-redux'
import type { GroupId } from 'types/group'
import {
  AccessibleIcon,
  Flex,
  IconButton,
  Link,
  Tooltip,
  Text,
} from 'components/ds'
import Skeleton from 'components/ui/skeleton'
import TextOverflow from 'components/ui/text-overflow'

import { InlineChannelSearch } from '../inline-channel-search'
import ShareChannelSubheader from './shared-channel-subheader'
import { getExplorerFirstPane } from '@disco/state-management/selectors/explorer'
import { BRIEF_LIST_PANE } from '@disco/constants/PaneTypes'
import { BriefListFilter } from 'features/briefs/components/brief-list-filter'
import { ExplorerPane } from '@disco/types/types/explorer'

type Props = {
  children?: React.ReactNode
  isLoading?: boolean
  title: string
  caption?: React.ReactNode
  dark?: boolean
  groupId?: GroupId
  pane?: ExplorerPane
  noTranslate?: boolean
  sticky?: boolean
}

const PaneHeader = ({
  children,
  isLoading = false,
  title,
  caption,
  dark,
  groupId,
  pane,
  noTranslate,
  sticky = false,
}: Props): React.ReactElement<'div'> => {
  const group = useSelector(state =>
    groupId
      ? getGroup(state, {
          groupId,
        })
      : null,
  )

  const firstPane = useSelector(getExplorerFirstPane)
  const isFirstPane = group?.id === firstPane?.groupId

  const plan = useSelector(getPlan)
  const isPlus = plan?.id === PLUS_ID
  const isSharedChannel = isChannelShared(group ?? undefined)
  const showSharedChannelWarning = isSharedChannel && isPlus
  const showSavedToDiscoHelpText = group?.group_type === SAVED_TO_DISCO

  return (
    <Flex
      direction="column"
      p="3"
      gap="3"
      className={clsx(styles.wrapper, { [styles.sticky]: sticky })}
    >
      <div
        className={clsx(styles.headBlock, {
          [styles.headDark]: dark,
        })}
      >
        <div className={styles.head}>
          <div className={styles.headContent}>
            <div
              className={styles.headTitle}
              translate={noTranslate ? 'no' : undefined}
            >
              {isLoading ? (
                <Skeleton />
              ) : (
                <Flex align="center" gap="2">
                  <div className={styles.headTitleText}>
                    <TextOverflow content={title} />
                  </div>
                  {showSharedChannelWarning && (
                    <Tooltip
                      content={
                        <>
                          You’re currently on our Plus plan and can only receive
                          invitations to channels. Please{' '}
                          <Link
                            className={styles.tooltipLink}
                            href="/settings/billing"
                          >
                            upgrade to our Pro plan
                          </Link>{' '}
                          to share your channels with other DISCOs.
                        </>
                      }
                    >
                      <IconButton size="1" variant="ghost" color="orange">
                        <AccessibleIcon label="Warning">
                          <MdOutlineWarningAmber size="18" />
                        </AccessibleIcon>
                      </IconButton>
                    </Tooltip>
                  )}

                  {showSavedToDiscoHelpText && (
                    <Tooltip
                      content={
                        <Theme appearance="dark" hasBackground={false}>
                          <Text as="p" size="1">
                            Find music that you like on other DISCO’s playlist
                            or Catalogs? Save it to your DISCO with just a
                            couple of clicks. All content will arrive here and
                            you can decide what to copy into your DISCO.{' '}
                            <Link
                              target="_blank"
                              rel="noreferrer"
                              href="https://school.disco.ac/article/save-to-disco"
                              underline="always"
                            >
                              Learn more
                            </Link>
                          </Text>
                        </Theme>
                      }
                    >
                      <IconButton size="1" variant="ghost" color="gray">
                        <AccessibleIcon label="Info">
                          <MdInfoOutline size="18" />
                        </AccessibleIcon>
                      </IconButton>
                    </Tooltip>
                  )}
                </Flex>
              )}
            </div>
            {group?.group_type === CUSTOM_CHANNEL && (
              <ShareChannelSubheader channel={group} />
            )}
          </div>

          <div className={styles.actions}>{children}</div>
        </div>

        {caption && (
          <>
            <div className={styles.captionDivider} />
            <div className={styles.headCaption}>{caption}</div>
          </>
        )}
      </div>

      {group?.group_type &&
        INLINE_SEARCHABLE_GROUP_TYPES.includes(group.group_type) &&
        isFirstPane && <InlineChannelSearch channel={group} />}
      {pane?.index === 0 && pane?.type === BRIEF_LIST_PANE && (
        <BriefListFilter pane={pane} />
      )}
    </Flex>
  )
}

export default PaneHeader
